/* Compact storefront UI for the existing ONETINYSHOP preview API.
   Audition selection is deliberately independent from purchase selection. */
(() => {
  if (customElements.get('ots-product-audio')) return;
  const clock = seconds => {
    const value = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
  };
  const normalize = value => String(value || '').trim().toLowerCase();
  customElements.define('ots-product-audio', class extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      this.audio = this.querySelector('audio');
      this.playButton = this.querySelector('.ots-audio-play');
      this.trigger = this.querySelector('.ots-audio-trigger');
      this.list = this.querySelector('.ots-audio-list');
      this.seek = this.querySelector('input[type="range"]');
      this.message = this.querySelector('.ots-audio-message');
      this.retry = this.querySelector('.ots-audio-retry');
      this.tracks = [];
      this.index = 0;
      this.playSequence = 0;
      const on = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: this.events.signal });
      on(this.playButton, 'click', () => this.togglePlayback());
      on(this.trigger, 'click', () => this.open(this.list.hidden));
      on(this.retry, 'click', () => this.loadTracks());
      on(this.list, 'click', event => {
        const option = event.target.closest('[data-index]');
        if (option) this.select(Number(option.dataset.index));
      });
      on(this, 'keydown', event => this.keydown(event));
      on(document, 'pointerdown', event => { if (!this.contains(event.target)) this.open(false, false); });
      on(this.seek, 'input', () => {
        if (Number.isFinite(this.audio.duration)) this.audio.currentTime = this.audio.duration * Number(this.seek.value) / 1000;
      });
      on(this.audio, 'timeupdate', () => this.updateProgress());
      on(this.audio, 'loadedmetadata', () => this.updateProgress());
      on(this.audio, 'play', () => {
        document.dispatchEvent(new CustomEvent('ots:preview-play', { detail: { player: this } }));
        this.setPlaying(true);
      });
      on(document, 'ots:preview-play', event => { if (event.detail?.player !== this) this.stop(); });
      on(document, 'play', event => { if (event.target !== this.audio && event.target instanceof HTMLMediaElement) this.stop(); }, { capture: true });
      on(this.audio, 'pause', () => this.setPlaying(false));
      on(this.audio, 'ended', () => this.setPlaying(false));
      on(this.audio, 'error', () => {
        this.setPlaying(false);
        this.message.textContent = 'This preview could not be played. Please try again.';
      });
      try {
        this.product = JSON.parse(this.querySelector('[data-product]').textContent);
        this.loadTracks();
      } catch {
        this.unavailable('Previews are temporarily unavailable.');
      }
    }
    disconnectedCallback() {
      this.stop();
      this.request?.abort();
      this.events?.abort();
      this.audio?.removeAttribute('src');
    }
    payload() {
      const product = this.product;
      const names = (product.options || []).map(option => typeof option === 'string' ? option : option.name);
      const position = names.findIndex(name => /tune|melody|song/i.test(name));
      const variants = product.variants || [];
      const tunes = position < 0 ? [] : [...new Set(variants.map(variant => variant.options?.[position] || variant[`option${position + 1}`]).filter(Boolean))];
      return {
        product: { id: product.id, title: product.title, handle: product.handle, url: `/products/${product.handle}`, price: product.price },
        variants, selected_variant: variants[0] || {}, selected_tune: tunes[0] || '', tune_options: tunes
      };
    }
    async loadTracks(preserve = false) {
      this.request?.abort();
      const request = this.request = new AbortController();
      const timeout = setTimeout(() => request.abort(), 12000);
      const selectedId = preserve ? this.tracks[this.index]?.track_id : null;
      this.playButton.disabled = true;
      this.trigger.disabled = true;
      this.retry.hidden = true;
      this.message.textContent = '';
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${this.dataset.apiBase}/api/product-preview`, {
          method: 'POST', mode: 'cors', credentials: 'omit', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.payload()), signal: request.signal
        });
        if (!response.ok) throw new Error('Preview request failed');
        const data = await response.json();
        if (!this.isConnected || this.request !== request) return false;
        const seen = new Set();
        // Only this listing's tunes belong in the selector; upgrade suggestions are not its variants.
        this.tracks = [data.current, ...(data.same_product_tunes || [])].filter(track => {
          if (!track?.track_id || !track.preview_url || seen.has(track.track_id)) return false;
          try { if (new URL(track.preview_url).protocol !== 'https:') return false; } catch { return false; }
          seen.add(track.track_id);
          return true;
        });
        const tuneOrder = this.payload().tune_options.map(normalize);
        this.tracks.sort((a, b) => {
          const ai = tuneOrder.indexOf(normalize(a.title));
          const bi = tuneOrder.indexOf(normalize(b.title));
          return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
        });
        this.index = Math.max(0, this.tracks.findIndex(track => track.track_id === selectedId));
        this.loadedAt = Date.now();
        this.render();
        return this.tracks.length > 0;
      } catch (error) {
        if (this.request === request && this.isConnected) this.unavailable('Previews are temporarily unavailable. Please try again.');
        return false;
      } finally {
        clearTimeout(timeout);
        if (this.request === request) this.removeAttribute('aria-busy');
      }
    }
    unavailable(text) {
      this.stop();
      this.open(false, false);
      this.querySelector('[data-track-name]').textContent = 'Preview unavailable';
      this.querySelector('[data-track-total]').textContent = '';
      this.querySelector('[data-track-position]').textContent = '';
      this.playButton.disabled = true;
      this.trigger.disabled = true;
      this.querySelector('[data-chevron]').hidden = true;
      this.message.textContent = text;
      this.retry.hidden = false;
    }
    render() {
      if (!this.tracks.length) {
        this.unavailable('No audio preview is available for this product yet.');
        this.retry.hidden = true;
        return;
      }
      this.list.replaceChildren();
      this.tracks.forEach((track, index) => {
        const item = document.createElement('li');
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(index === this.index));
        item.tabIndex = -1;
        item.dataset.index = String(index);
        const number = document.createElement('span');
        number.textContent = String(index + 1).padStart(2, '0');
        number.setAttribute('aria-hidden', 'true');
        const name = document.createElement('span');
        name.textContent = track.title || 'Melody';
        item.append(number, name);
        this.list.append(item);
      });
      const multiple = this.tracks.length > 1;
      this.querySelector('[data-track-total]').textContent = `${this.tracks.length} ${multiple ? 'tracks' : 'track'}`;
      this.querySelector('[data-track-name]').textContent = this.tracks[this.index].title;
      this.querySelector('[data-track-position]').textContent = multiple ? `${this.index + 1} of ${this.tracks.length}` : '';
      this.querySelector('[data-chevron]').hidden = !multiple;
      this.trigger.disabled = !multiple;
      this.trigger.setAttribute('aria-label', multiple ? `${this.tracks[this.index].title}, ${this.index + 1} of ${this.tracks.length} preview tracks. Choose another melody` : this.tracks[this.index].title);
      this.playButton.disabled = false;
      this.setPlaying(false);
    }
    open(value, focus = true) {
      const expanded = Boolean(value && this.tracks.length > 1);
      this.list.hidden = !expanded;
      this.trigger.setAttribute('aria-expanded', String(expanded));
      if (focus) {
        if (expanded) this.list.children[this.index]?.focus();
        else this.trigger.focus();
      }
    }
    keydown(event) {
      const isPicker = event.target === this.trigger || this.list.contains(event.target);
      if (!isPicker) return;
      if (event.key === 'Tab' && !this.list.hidden) { this.open(false); return; }
      if (event.key === 'Escape' && !this.list.hidden) { event.preventDefault(); this.open(false); return; }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (this.list.hidden) { this.open(true); return; }
        const active = Number(document.activeElement.dataset.index ?? this.index);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tracks.length - 1 : (active + (event.key === 'ArrowDown' ? 1 : -1) + this.tracks.length) % this.tracks.length;
        this.list.children[next]?.focus();
      } else if (['Enter', ' '].includes(event.key) && this.list.contains(event.target)) {
        event.preventDefault();
        this.select(Number(event.target.dataset.index));
      }
    }
    select(index) {
      if (!this.tracks[index]) return;
      this.stop();
      this.audio.removeAttribute('src');
      this.audio.load();
      this.index = index;
      this.render();
      this.updateProgress();
      this.message.textContent = `${this.tracks[index].title} selected for preview.`;
      this.open(false);
    }
    stop() {
      this.playSequence += 1;
      this.audio?.pause();
      this.playButton?.removeAttribute('aria-busy');
    }
    setPlaying(playing) {
      this.querySelector('[data-play-icon]').hidden = playing;
      this.querySelector('[data-pause-icon]').hidden = !playing;
      this.playButton.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${this.tracks[this.index]?.title || 'preview'}`);
      this.playButton.setAttribute('aria-pressed', String(playing));
    }
    async togglePlayback() {
      if (!this.audio.paused) { this.stop(); return; }
      if (this.playButton.getAttribute('aria-busy') === 'true') { this.stop(); return; }
      const sequence = ++this.playSequence;
      this.playButton.setAttribute('aria-busy', 'true');
      this.message.textContent = 'Loading preview…';
      try {
        let track = this.tracks[this.index];
        if (!track) return;
        const expires = Number(new URL(track.preview_url).searchParams.get('Expires') || 0) * 1000;
        if ((expires && expires < Date.now() + 30000) || Date.now() - this.loadedAt > 10 * 60 * 1000) {
          if (!await this.loadTracks(true)) return;
          track = this.tracks[this.index];
        }
        if (!this.isConnected || sequence !== this.playSequence) return;
        if (this.audio.src !== track.preview_url) this.audio.src = track.preview_url;
        if (this.audio.ended) this.audio.currentTime = 0;
        await this.audio.play();
        if (sequence !== this.playSequence) { this.audio.pause(); return; }
        this.message.textContent = '';
        // Same user-triggered play statistic as the original preview widget.
        fetch(`${this.dataset.apiBase}/api/tracks/${encodeURIComponent(track.track_id)}/play`, { method: 'POST', mode: 'cors', credentials: 'omit' }).catch(() => {});
      } catch {
        if (sequence === this.playSequence) {
          this.message.textContent = 'Unable to play this preview. Tap play to try again.';
          this.setPlaying(false);
        }
      } finally {
        if (sequence === this.playSequence) this.playButton.removeAttribute('aria-busy');
      }
    }
    updateProgress() {
      const duration = this.audio.duration;
      this.seek.disabled = !Number.isFinite(duration) || duration <= 0;
      this.seek.value = this.seek.disabled ? 0 : Math.round(this.audio.currentTime / duration * 1000);
      this.querySelector('[data-elapsed]').textContent = clock(this.audio.currentTime);
      this.querySelector('[data-duration]').textContent = this.seek.disabled ? '—:—' : clock(duration);
      this.seek.setAttribute('aria-valuetext', `${clock(this.audio.currentTime)} of ${this.seek.disabled ? 'unknown' : clock(duration)}`);
    }
  });
})();
